import { combineReducers } from 'redux'
import { FutureDataState } from 'components/features/future/types'
import { Reducer } from 'interfaces'
import {
  GET_FUTURE,
  PATCH_FUTURE_LAYER,
  POST_FUTURE_LAYER,
  DELETE_FUTURE_LAYER,
  GET_ASSUMPTIONS,
  PATCH_ASSUMPTIONS,
  RESET_POST_FUTURE_LAYER_ERROR,
  LAYER_CHECK,
  RESET_ASSUMPTIONS,
  COMPLETE_FUTURE_LAYER,
  PARTIAL_COMPLETE_FUTURE_LAYER,
  DropLayerCheckAction,
  GET_LAYER_HINTS,
} from 'components/features/future/actions'
import { createEmptyCollection } from 'helpers/normalizeHelpers'
import { createFetchableReducer, createEmptyFetchable } from 'helpers/loadableHelpers'
import { KILL_ALL_FLOATING_PANES, KILL_FLOATING_PANES } from 'actions/floatingPanesActions'

const fetchableRequestReducer = createFetchableReducer<null>()
const request: Reducer<FutureDataState['request']> = (prevState = createEmptyFetchable(), action) => {
  switch (action.type) {
    case RESET_ASSUMPTIONS:
    case PATCH_ASSUMPTIONS:
    case POST_FUTURE_LAYER:
    case PATCH_FUTURE_LAYER:
    case DELETE_FUTURE_LAYER:
    case COMPLETE_FUTURE_LAYER:
    case PARTIAL_COMPLETE_FUTURE_LAYER:
    case GET_FUTURE: {
      return fetchableRequestReducer(prevState, action)
    }
  }
  return prevState
}

const layers: Reducer<FutureDataState['layers']> = (prevState = createEmptyCollection(), action) => {
  switch (action.type) {
    case RESET_ASSUMPTIONS:
    case PATCH_ASSUMPTIONS:
    case POST_FUTURE_LAYER:
    case PATCH_FUTURE_LAYER:
    case DELETE_FUTURE_LAYER:
    case COMPLETE_FUTURE_LAYER:
    case PARTIAL_COMPLETE_FUTURE_LAYER:
    case GET_FUTURE: {
      if (action.meta !== 'success') {
        return prevState
      }
      return action.payload.response.layers
    }
  }
  return prevState
}

const INITIAL_NETWORTH_FORECAST = {
  series: [],
}
const networth_forecast: Reducer<FutureDataState['networth_forecast']> = (prevState = INITIAL_NETWORTH_FORECAST, action) => {
  switch (action.type) {
    case RESET_ASSUMPTIONS:
    case PATCH_ASSUMPTIONS:
    case POST_FUTURE_LAYER:
    case PATCH_FUTURE_LAYER:
    case DELETE_FUTURE_LAYER:
    case COMPLETE_FUTURE_LAYER:
    case PARTIAL_COMPLETE_FUTURE_LAYER:
    case GET_FUTURE: {
      if (action.meta !== 'success') {
        return prevState
      }
      return action.payload.response.networth_forecast
    }
  }
  return prevState
}

const INITIAL_NETWORTH_BREAKDOWN = {
  series: [],
}
const networth_breakdown: Reducer<FutureDataState['networth_breakdown']> = (prevState = INITIAL_NETWORTH_BREAKDOWN, action) => {
  switch (action.type) {
    case RESET_ASSUMPTIONS:
    case PATCH_ASSUMPTIONS:
    case POST_FUTURE_LAYER:
    case PATCH_FUTURE_LAYER:
    case DELETE_FUTURE_LAYER:
    case COMPLETE_FUTURE_LAYER:
    case PARTIAL_COMPLETE_FUTURE_LAYER:
    case GET_FUTURE: {
      if (action.meta !== 'success') {
        return prevState
      }
      return action.payload.response.networth_breakdown
    }
  }
  return prevState
}

const fetchableReducer = createFetchableReducer<null>()
const layerPostRequest: Reducer<FutureDataState['layerPostRequest']> = (prevState = createEmptyFetchable(), action) => {
  switch (action.type) {
    case POST_FUTURE_LAYER:
    case COMPLETE_FUTURE_LAYER:
    case PARTIAL_COMPLETE_FUTURE_LAYER:
    case PATCH_FUTURE_LAYER:
    case DELETE_FUTURE_LAYER: {
      return fetchableReducer(prevState, action)
    }
    case KILL_ALL_FLOATING_PANES:
    case KILL_FLOATING_PANES: {
      return createEmptyFetchable()
    }
    case RESET_POST_FUTURE_LAYER_ERROR: {
      const { data } = prevState

      return {
        ...createEmptyFetchable(),
        data,
      }
    }
  }

  return prevState
}

const assumptionsFetchableReducer = createFetchableReducer<NonNullable<FutureDataState['assumptions']['data']>>()
const assumptions: Reducer<FutureDataState['assumptions']> = (prevState = createEmptyFetchable(), action) => {
  switch (action.type) {
    case GET_ASSUMPTIONS: {
      return assumptionsFetchableReducer(prevState, action)
    }

    case RESET_POST_FUTURE_LAYER_ERROR: {
      return {
        ...prevState,
        errorCode: null,
      }
    }
  }
  return prevState
}

const layerCheck: Reducer<FutureDataState['layerCheck']> = (prevState = createEmptyFetchable(), action) => {
  switch (action.type) {
    case LAYER_CHECK: {
      if (action.meta === 'request') {
        return {
          ...prevState,
          isLoading: true,
        }
      }

      if (action.meta === 'fail') {
        const withErrorsMap = ('errorCode' in action.payload) ? { errorsMap: action.payload.errorsMap } : {}
        return {
          ...prevState,
          errorCode: action.payload.errorCode,
          isLoading: false,
          ...withErrorsMap,
        }
      }

      return {
        errorCode: null,
        isLoading: false,
        data: action.payload.response,
      }
    }
    case DropLayerCheckAction.type:
    case KILL_ALL_FLOATING_PANES:
    case KILL_FLOATING_PANES: {
      return createEmptyFetchable()
    }
  }
  return prevState
}

const layerHints: Reducer<FutureDataState['layerHints']> = (prevState = createEmptyFetchable(), action) => {
  switch (action.type) {
    case GET_LAYER_HINTS: {
      if (action.meta === 'request') {
        return {
          ...prevState,
          isLoading: true,
        }
      }

      if (action.meta === 'fail') {
        const withErrorsMap = ('errorCode' in action.payload) ? { errorsMap: action.payload.errorsMap } : {}
        return {
          ...prevState,
          errorCode: action.payload.errorCode,
          isLoading: false,
          ...withErrorsMap,
        }
      }

      return {
        errorCode: null,
        isLoading: false,
        data: {
          ...action.payload.response,
          slug: action.payload.params.slug,
        },
      }
    }
    case DropLayerCheckAction.type:
    case KILL_ALL_FLOATING_PANES:
    case KILL_FLOATING_PANES: {
      return createEmptyFetchable()
    }
  }
  return prevState
}

export const futureReducer = combineReducers<FutureDataState>({
  assumptions,
  request,
  layerPostRequest,
  layers,
  networth_forecast,
  networth_breakdown,
  layerCheck,
  layerHints,
})
